/**
 * SSE (Server-Sent Events) client utility for streaming execution output
 *
 * This utility provides a Svelte 5 reactive interface for subscribing to
 * real-time execution output streams from the backend.
 */

import { expertMode } from "./expertMode.svelte";
import { authManager } from "./auth.svelte";

/**
 * Event types for streaming execution output
 */
export type StreamingEventType =
  | "start"
  | "stdout"
  | "stderr"
  | "status"
  | "complete"
  | "error"
  | "command";

/**
 * Typed event data interfaces for each streaming event type
 */
export interface StartEventData {
  executionId: string;
  startedAt?: string;
}

export interface StdoutEventData {
  output: string;
}

export interface StderrEventData {
  output: string;
}

export interface StatusEventData {
  status: string;
}

export interface CompleteEventData {
  status: string;
  results?: unknown[];
  completedAt?: string;
  [key: string]: unknown;
}

export interface ErrorEventData {
  error: string;
  code?: string;
}

export interface CommandEventData {
  command: string;
}

/**
 * Union of all typed event data payloads
 */
export type StreamingEventData =
  | StartEventData
  | StdoutEventData
  | StderrEventData
  | StatusEventData
  | CompleteEventData
  | ErrorEventData
  | CommandEventData;

/**
 * Streaming event data structure
 */
export interface StreamingEvent {
  type: StreamingEventType;
  executionId: string;
  timestamp: string;
  data?: StreamingEventData;
}

/**
 * Connection status
 */
export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error";

/**
 * Execution stream state
 */
export interface ExecutionStreamState {
  status: ConnectionStatus;
  command: string | null;
  stdout: string;
  stderr: string;
  executionStatus: string | null;
  result: Record<string, unknown> | null;
  error: string | null;
  events: StreamingEvent[];
}

/**
 * Options for execution stream
 */
export interface ExecutionStreamOptions {
  /**
   * Maximum number of reconnection attempts
   * @default 3
   */
  maxReconnectAttempts?: number;

  /**
   * Delay between reconnection attempts in milliseconds
   * @default 1000
   */
  reconnectDelay?: number;

  /**
   * Whether to automatically reconnect on connection loss
   * @default true
   */
  autoReconnect?: boolean;

  /**
   * Callback when connection status changes
   */
  onStatusChange?: (status: ConnectionStatus) => void;

  /**
   * Callback when an event is received
   */
  onEvent?: (event: StreamingEvent) => void;

  /**
   * Callback when execution completes
   */
  onComplete?: (result: Record<string, unknown>) => void;

  /**
   * Callback when an error occurs
   */
  onError?: (error: string) => void;
}

const DEFAULT_OPTIONS: Required<
  Omit<
    ExecutionStreamOptions,
    "onStatusChange" | "onEvent" | "onComplete" | "onError"
  >
> = {
  maxReconnectAttempts: 3,
  reconnectDelay: 1000,
  autoReconnect: true,
};

/**
 * Create an execution stream for real-time output
 *
 * @param executionId - Unique execution identifier
 * @param options - Stream configuration options
 * @returns Reactive stream state and control methods
 */
export function useExecutionStream(
  executionId: string,
  options: ExecutionStreamOptions = {},
): ExecutionStream {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Reactive state
  const state = $state<ExecutionStreamState>({
    status: "disconnected",
    command: null,
    stdout: "",
    stderr: "",
    executionStatus: null,
    result: null,
    error: null,
    events: [],
  });

  let eventSource: EventSource | null = null;
  let reconnectAttempts = 0;
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  let connectInFlight: Promise<void> | null = null;
  let isManualDisconnect = false;

  /**
   * Update connection status
   */
  function setStatus(status: ConnectionStatus): void {
    state.status = status;
    options.onStatusChange?.(status);
  }

  /**
   * Obtain a single-use SSE ticket from the backend. The legacy `?token=` flow
   * was removed in C4 — passing the long-lived JWT in the URL leaked it into
   * access logs, browser history, and proxy caches.
   */
  async function obtainStreamTicket(): Promise<string | null> {
    const authHeader = authManager.getAuthHeader();
    if (!authHeader) {
      return null;
    }

    const response = await fetch(
      `/api/executions/${executionId}/stream-ticket`,
      {
        method: "POST",
        headers: { Authorization: authHeader },
      },
    );

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as { ticket?: string };
    return data.ticket ?? null;
  }

  /**
   * Build SSE URL with expert mode parameter and a single-use ticket.
   */
  function buildStreamUrl(ticket: string): string {
    const baseUrl = `/api/executions/${executionId}/stream`;
    const params = new URLSearchParams();

    if (expertMode.enabled) {
      params.set("expertMode", "true");
    }

    params.set("ticket", ticket);
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Handle incoming SSE event
   */
  function handleEvent(event: StreamingEvent): void {
    // Add to events array
    state.events.push(event);

    // Call event callback
    options.onEvent?.(event);

    // Update state based on event type — handles all types without throwing
    switch (event.type) {
      case "start":
        setStatus("connected");
        reconnectAttempts = 0; // Reset reconnect counter on successful connection
        break;

      case "command": {
        const cmdData = event.data as CommandEventData | undefined;
        if (cmdData && "command" in cmdData) {
          state.command = cmdData.command;
        }
        break;
      }

      case "stdout": {
        const stdoutData = event.data as StdoutEventData | undefined;
        if (stdoutData && "output" in stdoutData) {
          state.stdout += stdoutData.output;
        }
        break;
      }

      case "stderr": {
        const stderrData = event.data as StderrEventData | undefined;
        if (stderrData && "output" in stderrData) {
          state.stderr += stderrData.output;
        }
        break;
      }

      case "status": {
        const statusData = event.data as StatusEventData | undefined;
        if (statusData && "status" in statusData) {
          state.executionStatus = statusData.status;
        }
        break;
      }

      case "complete":
        state.result = event.data
          ? (event.data as Record<string, unknown>)
          : null;
        state.executionStatus = "success";
        setStatus("disconnected");
        if (event.data) {
          options.onComplete?.(event.data as Record<string, unknown>);
        }
        disconnect(); // Clean disconnect on completion
        break;

      case "error": {
        const errorData = event.data as ErrorEventData | undefined;
        if (errorData && "error" in errorData) {
          state.error = errorData.error;
          state.executionStatus = "failed";
        }
        setStatus("error");
        options.onError?.(state.error ?? "Unknown error");
        disconnect(); // Clean disconnect on error
        break;
      }
    }
  }

  /**
   * Connect to SSE stream. Internally async (must obtain a single-use ticket
   * from the backend before opening the EventSource) but exposed as
   * fire-and-forget so callers don't have to await — preserves the
   * pre-existing API and keeps Svelte component event handlers ergonomic.
   */
  function connect(): void {
    // Prevent multiple connections
    if (eventSource || connectInFlight) {
      return;
    }

    isManualDisconnect = false;
    setStatus("connecting");

    connectInFlight = connectAsync().finally(() => {
      connectInFlight = null;
    });
  }

  async function connectAsync(): Promise<void> {
    try {
      const ticket = await obtainStreamTicket();
      if (!ticket) {
        setStatus("error");
        state.error = "Failed to obtain stream ticket";
        options.onError?.(state.error);
        return;
      }

      // Caller may have disconnected while we awaited the ticket
      if (isManualDisconnect) {
        return;
      }

      const url = buildStreamUrl(ticket);
      eventSource = new EventSource(url);

      // Handle different event types
      const eventTypes: StreamingEventType[] = [
        "start",
        "stdout",
        "stderr",
        "status",
        "complete",
        "error",
        "command",
      ];

      for (const eventType of eventTypes) {
        eventSource.addEventListener(eventType, (e: MessageEvent): void => {
          try {
            const event = JSON.parse(e.data as string) as StreamingEvent;
            handleEvent(event);
          } catch (error) {
            console.error(`Failed to parse ${eventType} event:`, error);
          }
        });
      }

      // Handle connection open
      eventSource.addEventListener("open", (): void => {
        // Connection opened successfully
      });

      // Handle connection errors
      eventSource.addEventListener("error", (): void => {
        // Close the current connection
        if (eventSource) {
          eventSource.close();
          eventSource = null;
        }

        // Attempt reconnection if not manually disconnected
        if (
          !isManualDisconnect &&
          opts.autoReconnect &&
          reconnectAttempts < opts.maxReconnectAttempts
        ) {
          setStatus("reconnecting");
          reconnectAttempts++;

          const delay = opts.reconnectDelay * reconnectAttempts; // Exponential backoff

          reconnectTimeout = setTimeout((): void => {
            connect();
          }, delay);
        } else {
          setStatus("error");
          state.error =
            "Connection lost and maximum reconnection attempts reached";
        }
      });
    } catch (error) {
      console.error(
        `Failed to create SSE connection for execution ${executionId}:`,
        error,
      );
      setStatus("error");
      state.error =
        error instanceof Error ? error.message : "Failed to connect";
      options.onError?.(state.error);
    }
  }

  /**
   * Disconnect from SSE stream
   */
  function disconnect(): void {
    isManualDisconnect = true;

    // Clear reconnection timeout
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    // Close EventSource connection
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }

    setStatus("disconnected");
  }

  /**
   * Reconnect to SSE stream
   */
  function reconnect(): void {
    disconnect();
    reconnectAttempts = 0;
    connect();
  }

  /**
   * Clear accumulated output
   */
  function clearOutput(): void {
    state.stdout = "";
    state.stderr = "";
    state.events = [];
  }

  /**
   * Reset state to initial values
   */
  function reset(): void {
    state.command = null;
    state.stdout = "";
    state.stderr = "";
    state.executionStatus = null;
    state.result = null;
    state.error = null;
    state.events = [];
  }

  // Return reactive state and control methods
  return {
    // Reactive state (can be accessed directly in Svelte components)
    get status(): ConnectionStatus {
      return state.status;
    },
    get command(): string | null {
      return state.command;
    },
    get stdout(): string {
      return state.stdout;
    },
    get stderr(): string {
      return state.stderr;
    },
    get executionStatus(): string | null {
      return state.executionStatus;
    },
    get result(): Record<string, unknown> | null {
      return state.result;
    },
    get error(): string | null {
      return state.error;
    },
    get events(): StreamingEvent[] {
      return state.events;
    },
    get isConnected(): boolean {
      return state.status === "connected";
    },
    get isConnecting(): boolean {
      return state.status === "connecting" || state.status === "reconnecting";
    },
    get hasError(): boolean {
      return state.status === "error" || state.error !== null;
    },

    // Control methods
    connect,
    disconnect,
    reconnect,
    clearOutput,
    reset,
  };
}

/**
 * Type for the return value of useExecutionStream
 */
export interface ExecutionStream {
  // Reactive state
  readonly status: ConnectionStatus;
  readonly command: string | null;
  readonly stdout: string;
  readonly stderr: string;
  readonly executionStatus: string | null;
  readonly result: Record<string, unknown> | null;
  readonly error: string | null;
  readonly events: StreamingEvent[];
  readonly isConnected: boolean;
  readonly isConnecting: boolean;
  readonly hasError: boolean;

  // Control methods
  connect: () => void;
  disconnect: () => void;
  reconnect: () => void;
  clearOutput: () => void;
  reset: () => void;
}
